import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { X, Save, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Per-appointment notes editor. Renders one of three layouts based on the
 * server-resolved format:
 *
 *   - free_form: a single "Groomer notes" textarea (+ "Admin notes" when role=admin)
 *   - soap:      four labelled textareas (Subjective / Objective / Assessment / Plan)
 *   - custom:    N labelled textareas using admin-defined labels
 *
 * Storage: the underlying columns are plain text. For structured formats
 * (SOAP / custom) this component serializes the labelled fields into a
 * `{labels: [...], values: {...}, format: 'soap' | 'custom'}` JSON envelope
 * before saving and parses it back on load. Free-form notes are stored as
 * the raw string. The envelope shape is forward-compatible: if the admin
 * later changes the format, the previously-saved structured note still
 * round-trips because we store the labels alongside the values.
 *
 * Props:
 *   - appointmentId: string
 *   - userRole: 'admin' | 'groomer' (controls whether admin_notes is editable)
 *   - onClose: () => void
 *   - onSaved: optional () => void  — parent refresh hook
 */
export default function AppointmentNotesEditor({ appointmentId, userRole, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Format config from the server
  const [format, setFormat] = useState('free_form');
  const [labels, setLabels] = useState([]);

  // Free-form raw strings (also used as fallback when structured parse fails)
  const [groomerRaw, setGroomerRaw] = useState('');
  const [adminRaw, setAdminRaw] = useState('');

  // Structured values: { [label]: string }
  const [groomerStructured, setGroomerStructured] = useState({});
  const [adminStructured, setAdminStructured] = useState({});

  const isAdmin = userRole === 'admin';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API_URL}/appointments/${appointmentId}/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = res.data;
        setFormat(data.format || 'free_form');
        setLabels(Array.isArray(data.labels) ? data.labels : []);
        const { raw: gr, structured: gs } = parseStored(data.groomer_notes, data.format, data.labels);
        const { raw: ar, structured: as } = parseStored(data.admin_notes, data.format, data.labels);
        setGroomerRaw(gr);
        setGroomerStructured(gs);
        setAdminRaw(ar);
        setAdminStructured(as);
      } catch (e) {
        toast.error('Failed to load notes');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentId, onClose]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const body = {
        groomer_notes: serialize(format, labels, groomerStructured, groomerRaw),
      };
      if (isAdmin) {
        body.admin_notes = serialize(format, labels, adminStructured, adminRaw);
      }
      await axios.put(`${API_URL}/appointments/${appointmentId}/notes`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Notes saved');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="text-primary-600" size={22} />
            <h2 className="text-xl font-bold">Appointment Notes</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 ml-2">
              Format: {format === 'soap' ? 'SOAP' : format === 'custom' ? 'Custom' : 'Free-form'}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Groomer notes — visible to all staff */}
            <NotesSection
              title="Groomer / Provider notes"
              hint="Visible to admin and the assigned employee."
              format={format}
              labels={labels}
              raw={groomerRaw}
              setRaw={setGroomerRaw}
              structured={groomerStructured}
              setStructured={setGroomerStructured}
            />

            {/* Admin notes — admin-only */}
            {isAdmin && (
              <NotesSection
                title="Admin-only notes"
                hint="Private to admins. Not visible to employees."
                format={format}
                labels={labels}
                raw={adminRaw}
                setRaw={setAdminRaw}
                structured={adminStructured}
                setStructured={setAdminStructured}
                accent="red"
              />
            )}
          </div>
        )}

        <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button onClick={onClose} disabled={saving} className="btn btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving || loading} className="btn btn-primary flex items-center space-x-2">
            <Save size={16} />
            <span>{saving ? 'Saving…' : 'Save notes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// --- internals -----------------------------------------------------------

function NotesSection({ title, hint, format, labels, raw, setRaw, structured, setStructured, accent }) {
  const headerClass = accent === 'red'
    ? 'text-red-700 font-semibold text-sm flex items-center'
    : 'text-gray-800 font-semibold text-sm flex items-center';
  const badgeClass = accent === 'red'
    ? 'bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs mr-2'
    : 'bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs mr-2';

  return (
    <div>
      <h3 className={headerClass}>
        {accent === 'red' ? <span className={badgeClass}>ADMIN ONLY</span> : <span className={badgeClass}>STAFF</span>}
        {title}
      </h3>
      {hint && <p className="text-xs text-gray-500 mt-1 mb-2">{hint}</p>}

      {format === 'free_form' && (
        <textarea
          rows="6"
          className="input"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Type your notes here…"
        />
      )}

      {format !== 'free_form' && labels.length > 0 && (
        <div className="space-y-3">
          {labels.map((label) => (
            <div key={label}>
              <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
              <textarea
                rows="3"
                className="input"
                value={structured[label] || ''}
                onChange={(e) => setStructured({ ...structured, [label]: e.target.value })}
                placeholder={`Notes for ${label.toLowerCase()}…`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Parse a stored notes string. Structured envelopes round-trip; legacy or
 * format-mismatched content gracefully degrades to a single "raw" string
 * which the editor surfaces in a free-form box (so nothing is ever lost).
 */
function parseStored(stored, format, labels) {
  const empty = { raw: '', structured: {} };
  if (!stored || typeof stored !== 'string') return empty;

  const trimmed = stored.trim();
  if (!trimmed.startsWith('{')) {
    return { raw: stored, structured: {} };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.values === 'object') {
      // Use the labels stored alongside the values (so a format change
      // doesn't drop content). If the current format's labels overlap, we
      // pre-fill those; orphan values become a "_legacy" raw blob the
      // editor renders below.
      const result = {};
      for (const lbl of labels || []) {
        if (typeof parsed.values[lbl] === 'string') result[lbl] = parsed.values[lbl];
      }
      // Orphan keys (saved under a previous format / labels list) get
      // concatenated into raw so they're still visible if format changes.
      const orphans = Object.entries(parsed.values)
        .filter(([k]) => !labels || !labels.includes(k))
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n');
      return { raw: orphans, structured: result };
    }
  } catch (_) { /* fall through */ }

  return { raw: stored, structured: {} };
}

function serialize(format, labels, structured, raw) {
  if (format === 'free_form') return raw || '';
  // Structured: keep labels alongside values so a later format change
  // doesn't orphan content.
  const cleanValues = {};
  for (const lbl of labels) {
    const v = structured[lbl];
    if (typeof v === 'string' && v.length > 0) cleanValues[lbl] = v;
  }
  // If raw also has content (e.g. legacy free-form text), preserve it under
  // a sentinel key so it isn't silently lost on save.
  if (raw && raw.trim()) cleanValues._legacy = raw;
  if (Object.keys(cleanValues).length === 0) return '';
  return JSON.stringify({ format, labels, values: cleanValues });
}
